// Vanilla Canvas2D renderer for the "Birds in Flight" template.
// Clean, simplified birds with flapping wings; slow-drifting clouds over a two-mountain skyline.

function seededRandom(seed) {
  let t = seed + 0x6D2B79F5;
  return function () {
    t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function hashId(id) {
  let h = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h >>> 0;
}

const BIRD_SPECIES = [
  { body: '#2d2d2d', wing: '#1a1a1a', accent: '#404040', name: 'Raven' },
  { body: '#8b7355', wing: '#6b5a42', accent: '#a08868', name: 'Sparrow' },
  { body: '#c0a060', wing: '#a08040', accent: '#e0c070', name: 'Finch' },
  { body: '#5a7a9a', wing: '#405a7a', accent: '#7a9ac0', name: 'Blue Jay' },
  { body: '#d4a848', wing: '#b89038', accent: '#e8c060', name: 'Goldfinch' },
];

function drawBird(ctx, b, t, isHover) {
  const pos = b.position(t);
  const x = pos.x;
  const y = pos.y;
  b._screenX = x;
  b._screenY = y;

  const age = performance.now() - b.spawnTime;
  const spawnScale = Math.min(1, age / 500);
  const scale = (isHover ? 1.25 : 1) * spawnScale;
  const wingSpan = b.wingSpan * scale;
  const bodyLen = b.bodyLen * scale;
  const wingDraw = wingSpan * 0.8; // drawn wing length, shorter than the full wingSpan used for hit-testing
  // Flap phase: swings between roughly +40deg (wings up) and -20deg (wings down)
  const wingPhase = Math.sin(t * b.wingFreq + b.wingPhase) * 0.9;

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(b.heading(t));

  // Wings (drawn first so the body overlaps the wing roots, like a real silhouette)
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(-bodyLen * 0.05, 0);
    ctx.rotate(wingPhase * side);
    const wgrad = ctx.createLinearGradient(0, 0, wingDraw * side, -wingDraw * 0.2);
    wgrad.addColorStop(0, b.color.body);
    wgrad.addColorStop(0.55, b.color.wing);
    wgrad.addColorStop(1, b.color.accent);
    ctx.fillStyle = wgrad;
    ctx.strokeStyle = b.color.wing;
    ctx.lineWidth = Math.max(0.5, scale * 0.6);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(wingDraw * 0.55 * side, -wingDraw * 0.32, wingDraw * side, -wingDraw * 0.04);
    ctx.quadraticCurveTo(wingDraw * 0.5 * side, wingDraw * 0.06, wingDraw * 0.12 * side, wingDraw * 0.07);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // Body
  const bodyGrad = ctx.createLinearGradient(-bodyLen * 0.5, 0, bodyLen * 0.4, 0);
  bodyGrad.addColorStop(0, b.color.wing);
  bodyGrad.addColorStop(0.4, b.color.body);
  bodyGrad.addColorStop(1, b.color.accent);
  ctx.fillStyle = bodyGrad;
  ctx.beginPath();
  ctx.ellipse(0, 0, bodyLen * 0.45, bodyLen * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();

  // Head
  ctx.beginPath();
  ctx.ellipse(bodyLen * 0.35, 0, bodyLen * 0.15, bodyLen * 0.12, 0, 0, Math.PI * 2);
  ctx.fillStyle = b.color.body;
  ctx.fill();

  // Eye
  ctx.beginPath();
  ctx.arc(bodyLen * 0.42, -bodyLen * 0.03, Math.max(1, bodyLen * 0.03), 0, Math.PI * 2);
  ctx.fillStyle = '#0a0a0a';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(bodyLen * 0.44, -bodyLen * 0.04, Math.max(0.5, bodyLen * 0.01), 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.fill();

  // Beak
  ctx.beginPath();
  ctx.moveTo(bodyLen * 0.5, -bodyLen * 0.02);
  ctx.lineTo(bodyLen * 0.65, 0);
  ctx.lineTo(bodyLen * 0.5, bodyLen * 0.02);
  ctx.closePath();
  ctx.fillStyle = b.color.accent;
  ctx.fill();

  // Tail feathers (kept simple, fanned)
  const tailLen = bodyLen * 0.35;
  for (let i = -2; i <= 2; i++) {
    const tailAngle = i * 0.18;
    ctx.save();
    ctx.translate(-bodyLen * 0.4, 0);
    ctx.rotate(tailAngle);
    const tgrad = ctx.createLinearGradient(0, 0, tailLen, 0);
    tgrad.addColorStop(0, b.color.wing);
    tgrad.addColorStop(1, b.color.accent);
    ctx.fillStyle = tgrad;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(tailLen * 0.5, -bodyLen * 0.08, tailLen, 0);
    ctx.quadraticCurveTo(tailLen * 0.5, bodyLen * 0.08, 0, 0);
    ctx.fill();
    ctx.restore();
  }

  // Hover highlight
  if (isHover) {
    ctx.beginPath();
    ctx.ellipse(0, 0, bodyLen * 0.8, wingSpan * 0.6, 0, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.6)';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  ctx.restore();
}

export function createBirdsStage(canvas, opts = {}) {
  const ctx = canvas.getContext('2d');
  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const birds = new Map();
  const clouds = [];
  let hoverId = null;
  let selectCb = opts.onSelect || null;
  let hoverCb = opts.onHover || null;
  let running = true;
  let rafId = null;
  const startTime = performance.now();
  let lastNow = startTime;

  let bgImage = null;
  let bgImageReady = false;
  if (opts.backgroundImageUrl) {
    bgImage = new Image();
    bgImage.crossOrigin = 'anonymous';
    bgImage.onload = () => { bgImageReady = true; };
    bgImage.src = opts.backgroundImageUrl;
  }

  function drawCoverImage(img) {
    const scale = Math.max(width / img.width, height / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
    ctx.fillStyle = 'rgba(200, 220, 240, 0.15)';
    ctx.fillRect(0, 0, width, height);
  }

  function resize() {
    const rect = canvas.parentElement.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (clouds.length === 0) {
      const rnd = seededRandom(100);
      for (let i = 0; i < 8; i++) {
        clouds.push({
          x: rnd(),
          y: rnd() * 0.35,
          width: 0.15 + rnd() * 0.15,
          height: 0.04 + rnd() * 0.03,
          // 3x slower than the previous pass
          speed: 0.0004 + rnd() * 0.00073,
          opacity: 0.15 + rnd() * 0.15,
        });
      }
    }
    layoutBirds();
  }

  function skyBounds() {
    const pad = 40;
    return {
      left: pad,
      top: pad,
      right: width - pad,
      bottom: height * 0.72,
    };
  }

  function wrapAngle(a) {
    while (a > Math.PI) a -= Math.PI * 2;
    while (a < -Math.PI) a += Math.PI * 2;
    return a;
  }

  function layoutBirds() {
    const box = skyBounds();
    for (const b of birds.values()) {
      if (!b.laidOut) {
        b.x = box.left + b.rnd() * Math.max(1, box.right - box.left);
        b.y = box.top + b.rnd() * Math.max(1, box.bottom - box.top);
        b.laidOut = true;
      }
      b.x = Math.min(box.right, Math.max(box.left, b.x || box.left));
      b.y = Math.min(box.bottom, Math.max(box.top, b.y || box.top));
    }
  }

  function updateBirds(dt) {
    const box = skyBounds();
    const margin = 70;
    for (const b of birds.values()) {
      if (!b.laidOut) continue;
      b.wanderTimer -= dt;
      if (b.wanderTimer <= 0) {
        b.wanderTimer = 0.8 + b.rnd() * 2.2;
        b.targetAngle = b.rnd() * Math.PI * 2;
      }
      if (b.x < box.left + margin) b.targetAngle = 0;
      else if (b.x > box.right - margin) b.targetAngle = Math.PI;
      if (b.y < box.top + margin) b.targetAngle = Math.PI / 2;
      else if (b.y > box.bottom - margin) b.targetAngle = -Math.PI / 2;
      const diff = wrapAngle(b.targetAngle - b.angle);
      b.angle += Math.max(-b.turnRate * dt, Math.min(b.turnRate * dt, diff));
      b.x += Math.cos(b.angle) * b.speed * dt;
      b.y += Math.sin(b.angle) * b.speed * dt;
      b.x = Math.min(box.right, Math.max(box.left, b.x));
      b.y = Math.min(box.bottom, Math.max(box.top, b.y));
    }
  }

  function ensureBird(msg) {
    if (birds.has(msg.id)) return;
    const seed = hashId(msg.id);
    const rnd = seededRandom(seed);
    const species = BIRD_SPECIES[seed % BIRD_SPECIES.length];
    birds.set(msg.id, {
      id: msg.id,
      sender_name: msg.sender_name,
      message_body: msg.message_body,
      rnd,
      color: species,
      wingSpan: 34 + rnd() * 20,
      bodyLen: 16 + rnd() * 9,
      wingFreq: 1.5 + rnd() * 1.5,
      wingPhase: rnd() * Math.PI * 2,
      freqX: 0.05 + rnd() * 0.06,
      freqY: 0.07 + rnd() * 0.08,
      phaseX: rnd() * Math.PI * 2,
      phaseY: rnd() * Math.PI * 2,
      spawnTime: performance.now(),
      laidOut: false,
      x: width / 2,
      y: height / 2,
      angle: rnd() * Math.PI * 2,
      targetAngle: rnd() * Math.PI * 2,
      wanderTimer: rnd(),
      speed: 42 + rnd() * 48,
      turnRate: 0.9 + rnd() * 1.1,
      position() {
        return { x: this.x, y: this.y };
      },
      heading() {
        return this.angle;
      },
    });
  }

  function setMessages(messages) {
    for (const msg of messages) ensureBird(msg);
    layoutBirds();
  }

  // Soft, irregular snow cap that follows the mountain's slopes with a
  // natural wavy edge (smoothed through quadratic curves) rather than
  // right-angle stair steps, plus a couple of loose trailing snow patches
  // further down the slope for a more real look.
  function drawSnowCap(peakX, peakY, leftBaseX, rightBaseX, horizonY, seed) {
    const rnd = seededRandom(seed);
    const mountainH = Math.max(1, horizonY - peakY);
    const capDepth = mountainH * (0.2 + rnd() * 0.06);
    const leftSlope = (peakX - leftBaseX) / mountainH;
    const rightSlope = (rightBaseX - peakX) / mountainH;
    const pointsPerSide = 6;

    function sidePoints(slope, sign) {
      const pts = [];
      for (let i = 0; i <= pointsPerSide; i++) {
        const frac = i / pointsPerSide;
        const y = peakY + frac * capDepth;
        const idealX = peakX + sign * slope * (frac * capDepth);
        // Irregular but smooth perpendicular wobble, tapering to zero at the
        // peak and at the base (so it stays anchored, not a random blob)
        const amp = capDepth * 0.1 * Math.sin(frac * Math.PI);
        const wobble = (rnd() - 0.5) * 2 * amp;
        pts.push({ x: idealX + wobble, y });
      }
      return pts;
    }

    const left = sidePoints(leftSlope, -1);
    const right = sidePoints(rightSlope, 1);

    ctx.beginPath();
    ctx.moveTo(peakX, peakY);
    for (let i = 1; i < left.length; i++) {
      const p0 = left[i - 1];
      const p1 = left[i];
      const mx = (p0.x + p1.x) / 2;
      const my = (p0.y + p1.y) / 2;
      ctx.quadraticCurveTo(p0.x, p0.y, mx, my);
    }
    const lb = left[left.length - 1];
    ctx.lineTo(lb.x, lb.y);

    // Gentle wavy base connecting the two sides
    const rb = right[right.length - 1];
    const bendX = (lb.x + rb.x) / 2;
    const bendY = (lb.y + rb.y) / 2 + (rnd() - 0.5) * capDepth * 0.12;
    ctx.quadraticCurveTo(bendX, bendY, rb.x, rb.y);

    for (let i = right.length - 1; i > 0; i--) {
      const p0 = right[i];
      const p1 = right[i - 1];
      const mx = (p0.x + p1.x) / 2;
      const my = (p0.y + p1.y) / 2;
      ctx.quadraticCurveTo(p0.x, p0.y, mx, my);
    }
    ctx.lineTo(peakX, peakY);
    ctx.closePath();

    ctx.fillStyle = '#ffffff';
    ctx.fill();

    // Soft directional shading for a bit of volume (smooth, not blocky)
    const shadeGrad = ctx.createLinearGradient(peakX, peakY, rb.x, rb.y);
    shadeGrad.addColorStop(0, 'rgba(206,218,230,0)');
    shadeGrad.addColorStop(1, 'rgba(196,210,224,0.45)');
    ctx.fillStyle = shadeGrad;
    ctx.fill();

    ctx.strokeStyle = 'rgba(210,222,234,0.6)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  function drawMountains() {
    const horizonY = height * 0.8;

    // Ground below horizon
    ctx.fillStyle = '#dbe6ec';
    ctx.fillRect(0, horizonY, width, Math.max(0, height - horizonY));

    // Far mountain (hazier, lighter - reads as farther away)
    const farLeftX = -width * 0.05;
    const farRightX = width * 0.5;
    const farPeakX = width * 0.24;
    const farPeakY = horizonY - height * 0.3;
    ctx.fillStyle = 'rgba(150,166,188,0.55)';
    ctx.beginPath();
    ctx.moveTo(farLeftX, horizonY);
    ctx.lineTo(farPeakX, farPeakY);
    ctx.lineTo(farRightX, horizonY);
    ctx.closePath();
    ctx.fill();
    drawSnowCap(farPeakX, farPeakY, farLeftX, farRightX, horizonY, 501);

    // Near mountain (darker, closer, taller)
    const nearLeftX = width * 0.34;
    const nearRightX = width * 1.05;
    const nearPeakX = width * 0.68;
    const nearPeakY = horizonY - height * 0.4;
    ctx.fillStyle = 'rgba(108,128,152,0.75)';
    ctx.beginPath();
    ctx.moveTo(nearLeftX, horizonY);
    ctx.lineTo(nearPeakX, nearPeakY);
    ctx.lineTo(nearRightX, horizonY);
    ctx.closePath();
    ctx.fill();
    drawSnowCap(nearPeakX, nearPeakY, nearLeftX, nearRightX, horizonY, 907);

    // Soft atmospheric haze where mountains meet the sky
    const haze = ctx.createLinearGradient(0, horizonY - height * 0.15, 0, horizonY);
    haze.addColorStop(0, 'rgba(230,238,244,0)');
    haze.addColorStop(1, 'rgba(230,238,244,0.5)');
    ctx.fillStyle = haze;
    ctx.fillRect(0, horizonY - height * 0.15, width, height * 0.15);
  }

  function drawBackground(t) {
    if (bgImageReady) {
      drawCoverImage(bgImage);
      return;
    }

    // Sky gradient
    const grad = ctx.createLinearGradient(0, 0, 0, height);
    grad.addColorStop(0, '#87ceeb');
    grad.addColorStop(0.3, '#a8d4f0');
    grad.addColorStop(0.6, '#cce0f5');
    grad.addColorStop(1, '#e8f0f8');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Sun
    const sunX = width * 0.85;
    const sunY = height * 0.18;
    const sunR = Math.min(width, height) * 0.08;
    const sunGrad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR * 4);
    sunGrad.addColorStop(0, 'rgba(255,255,200,0.6)');
    sunGrad.addColorStop(0.5, 'rgba(255,240,180,0.3)');
    sunGrad.addColorStop(1, 'rgba(255,230,150,0)');
    ctx.fillStyle = sunGrad;
    ctx.fillRect(sunX - sunR * 4, sunY - sunR * 4, sunR * 8, sunR * 8);
    ctx.beginPath();
    ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2);
    ctx.fillStyle = '#fffef0';
    ctx.fill();

    // Mountains sit on the horizon, behind the clouds
    drawMountains();

    // Clouds
    for (const c of clouds) {
      c.x = (c.x + c.speed) % 1.2;
      if (c.x > 1.1) c.x = -0.1;
      const cx = c.x * width;
      const cy = c.y * height;
      const cw = c.width * width;
      const ch = c.height * height;

      ctx.fillStyle = `rgba(255,255,255,${c.opacity})`;
      ctx.beginPath();
      ctx.ellipse(cx, cy, cw, ch, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(cx + cw * 0.6, cy - ch * 0.3, cw * 0.7, ch * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(cx - cw * 0.5, cy - ch * 0.2, cw * 0.6, ch * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function frame(now) {
    if (!running) return;
    const dt = Math.min(0.05, (now - lastNow) / 1000);
    lastNow = now;
    const t = (now - startTime) / 1000;
    updateBirds(dt);
    drawBackground(t);
    for (const b of birds.values()) {
      drawBird(ctx, b, t, b.id === hoverId);
    }
    rafId = requestAnimationFrame(frame);
  }

  function hitTest(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    let found = null;
    let bestDist = Infinity;
    for (const b of birds.values()) {
      const dx = (b._screenX ?? b.x) - px;
      const dy = (b._screenY ?? b.y) - py;
      const dist = Math.hypot(dx, dy);
      const hitR = Math.max(30, b.wingSpan * 0.6);
      if (dist <= hitR && dist < bestDist) {
        bestDist = dist;
        found = b;
      }
    }
    return found ? { obj: found, x: px, y: py } : null;
  }

  function handleMove(e) {
    const hit = hitTest(e.clientX, e.clientY);
    const id = hit ? hit.obj.id : null;
    if (id !== hoverId) {
      hoverId = id;
      canvas.style.cursor = id ? 'pointer' : 'default';
      if (hoverCb) hoverCb(hit ? { id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y } : null);
    } else if (hit && hoverCb) {
      hoverCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body, x: hit.x, y: hit.y });
    }
  }

  function handleClick(e) {
    const hit = hitTest(e.clientX, e.clientY);
    if (hit && selectCb) {
      selectCb({ id: hit.obj.id, sender_name: hit.obj.sender_name, message_body: hit.obj.message_body }, { x: hit.x, y: hit.y });
    }
  }

  function handleLeave() {
    if (hoverId !== null) {
      hoverId = null;
      canvas.style.cursor = 'default';
      if (hoverCb) hoverCb(null);
    }
  }

  canvas.addEventListener('pointermove', handleMove);
  canvas.addEventListener('click', handleClick);
  canvas.addEventListener('pointerleave', handleLeave);

  resize();
  rafId = requestAnimationFrame(frame);

  return {
    setMessages,
    onSelect(cb) { selectCb = cb; },
    onHover(cb) { hoverCb = cb; },
    resize,
    destroy() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      canvas.removeEventListener('pointermove', handleMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('pointerleave', handleLeave);
    },
  };
}
